import React, { useMemo } from 'react';
import { Box, Container, IconButton, Link, Stack, Tooltip, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStorefrontContext } from 'context/StorefrontContext';
import { ROUTES } from 'config/routes';
import { SOCIAL_NETWORKS } from 'config/socialNetworks';
import { NavItem } from 'types/storefront';
import { buildInfo } from 'config/buildInfo';
import { resolveSiteName } from 'utils/themeDisplay';
import { sanitizeFooterHtml } from 'utils/sanitizeHtml';

const defaultFooter: NavItem[] = [
  { labelKey: 'nav.catalog', url: ROUTES.CATALOG },
  { labelKey: 'nav.promotions', url: ROUTES.PROMOTIONS },
];

const phoneCallHref = (phone: string) => {
  const compact = phone.replace(/[^\d+]/g, '');
  return `tel:${compact || phone.trim()}`;
};

const mapsHref = (address: string) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address.trim())}`;

const NeosCopyright: React.FC = () => (
  <Stack spacing={0.25} alignItems={{ xs: 'flex-start', sm: 'flex-end' }}>
    <Typography variant="body2" color="grey.500">
      Copyright © {new Date().getFullYear()}{' '}
      <Link href="https://www.neos.com.ar" target="_blank" rel="noopener noreferrer" color="inherit" underline="hover">
        neos.com.ar
      </Link>
      . Todos los derechos reservados.
    </Typography>
    <Typography variant="caption" color="grey.600">
      v{buildInfo.version} · Build {buildInfo.buildNumber}
    </Typography>
  </Stack>
);

const Footer: React.FC = () => {
  const { t } = useTranslation('common');
  const { navigation, theme } = useStorefrontContext();
  const items = navigation.footer.length > 0 ? navigation.footer : defaultFooter;
  const siteName = resolveSiteName(theme);
  const contact = theme?.contact;
  const socialLinks = SOCIAL_NETWORKS.flatMap((network) => {
    const url = contact?.[network.key]?.trim();
    return url ? [{ ...network, url }] : [];
  });
  const customFooterHtml = useMemo(
    () => (theme?.footer?.customEnabled && theme.footer.customHtml ? sanitizeFooterHtml(theme.footer.customHtml) : ''),
    [theme?.footer?.customEnabled, theme?.footer?.customHtml]
  );

  if (customFooterHtml) {
    return (
      <Box component="footer" sx={{ mt: 'auto' }}>
        <Box className="store-custom-footer" dangerouslySetInnerHTML={{ __html: customFooterHtml }} />
        <Box sx={{ bgcolor: 'grey.900', py: 1.5 }}>
          <Container maxWidth="lg">
            <NeosCopyright />
          </Container>
        </Box>
      </Box>
    );
  }

  return (
    <Box component="footer" sx={{ bgcolor: 'grey.900', color: 'grey.100', mt: 'auto', py: 4 }}>
      <Container maxWidth="lg">
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="space-between">
          <Box>
            <Typography variant="h6">{siteName}</Typography>
            {contact && (
              <Stack spacing={0.5} mt={1.5}>
                {contact.phone && (
                  <Link href={phoneCallHref(contact.phone)} color="inherit" underline="hover" variant="body2">
                    {contact.phone}
                  </Link>
                )}
                {contact.email && (
                  <Link href={`mailto:${contact.email}`} color="inherit" underline="hover" variant="body2">
                    {contact.email}
                  </Link>
                )}
                {contact.address && (
                  <Link
                    href={mapsHref(contact.address)}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="inherit"
                    underline="hover"
                    variant="body2"
                  >
                    {contact.address}
                  </Link>
                )}
                {contact.hours && (
                  <Box>
                    <Typography variant="caption" color="grey.500" component="p">
                      {t('footer.hours')}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                      {contact.hours}
                    </Typography>
                  </Box>
                )}
                {socialLinks.length > 0 && (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ pt: 0.5, ml: -1 }}>
                    {socialLinks.map(({ key, label, icon: Icon, url }) => (
                      <Tooltip key={key} title={label}>
                        <IconButton
                          component="a"
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={label}
                          size="small"
                          sx={{ color: 'grey.300', '&:hover': { color: 'common.white' } }}
                        >
                          <Icon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ))}
                  </Stack>
                )}
              </Stack>
            )}
          </Box>
          <Stack spacing={1.25} alignItems="flex-start">
            {items.map((item) => (
              <Link
                key={item.url}
                component={RouterLink}
                to={item.url}
                color="inherit"
                underline="hover"
              >
                {item.label || (item.labelKey ? t(item.labelKey) : item.url)}
              </Link>
            ))}
          </Stack>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          mt={3}
        >
          <Typography variant="body2" color="grey.500">
            © {new Date().getFullYear()} {siteName}
          </Typography>
          <NeosCopyright />
        </Stack>
      </Container>
    </Box>
  );
};

export default Footer;
